<script setup lang="ts">
import { toRefs, ref, watchEffect, computed } from 'vue';
import Calendar from 'primevue/calendar';
import { useOrganizationContractsStore } from '@/store';
import { useDateTime } from '@/composables';
import { ClinicianContractDetails } from '@/types';
import { EMIT } from '@/enum';

interface PropsInterface {
  clinician: ClinicianContractDetails;
}

const removeCancelEmit = `${EMIT.REMOVE}-${EMIT.CANCEL}`;
const emit = defineEmits<{
  (e: typeof removeCancelEmit): void;
  (e: EMIT.REMOVE, endDates: Record<string, Date>): void;
}>();
const props = defineProps<PropsInterface>();
const { clinician } = toRefs(props);
const contractStore = useOrganizationContractsStore();
const endDate = ref<Date>();
const serviceEndDate = ref<Date>();
const eventsRemoved = ref<number>(0);
const today = ref<Date>(new Date());

const onCancel = () => emit(EMIT.CANCEL);

const onRemove = () =>
  emit(EMIT.REMOVE, { endDate: endDate.value as Date, serviceEndDate: serviceEndDate.value as Date });

const disableButtons = computed(() => !endDate.value || !serviceEndDate.value);

watchEffect(async () => {
  if (serviceEndDate.value) {
    const day = serviceEndDate.value.getDate() - 1;
    const month = serviceEndDate.value.getMonth();
    const year = serviceEndDate.value.getFullYear();
    const checkDate = new Date(year, month, day);
    const { events_count } = await contractStore.checkEventToRemove({
      clinician_id: clinician.value.person?.id,
      start_at: useDateTime().formatDateForApi(contractStore.contract.start_at),
      end_at: useDateTime().formatDateForApi(checkDate),
    });
    eventsRemoved.value = events_count;
  }
});
</script>

<template>
  <ConfirmAction
    header="Remove Service Provider"
    class="remove-provider-modal"
    :style="{ width: '30rem' }"
    content-class="pb-0"
    :show-footer="true"
    :disable-accept="disableButtons"
    accept-label="Remove Provider"
    @accept="onRemove"
    @cancel="onCancel"
  >
    <div class="card">
      <div class="mb-2 field required">
        <label for="end_date">
          End Date
          <span class="sub-text">(Service Provider will be removed from the contract on: )</span>
        </label>
        <Calendar
          id="end_date"
          v-model="endDate"
          :min-date="today"
          class="w-full"
          placeholder="01/01/2001"
          date-format="mm/dd/yy"
        />
      </div>
      <div class="mb-2 field required">
        <label for="service_end_date">
          Service End Date
          <span class="sub-text">(Service Provider cannot create or document events after: )</span>
        </label>
        <Calendar
          id="service_end_date"
          v-model="serviceEndDate"
          :min-date="today"
          class="w-full"
          placeholder="01/01/2001"
          date-format="mm/dd/yy"
        />
      </div>
    </div>
    <div v-if="endDate && serviceEndDate" class="mt-4 flex">
      <Icon name="warning" class="confirm-dialog-icon text-gray-600" />
      <div class="ml-3">
        <p class="remove-notice mb-2">
          Removing
          <b>{{ clinician.person?.display_name }}</b>
          from this contract will remove
          <b>{{ eventsRemoved }}</b>
          events scheduled after
          <b>{{ Intl.DateTimeFormat().format(serviceEndDate) }}</b>
          .
        </p>
        <p class="remove-notice mb-2">
          <b>{{ clinician.person?.display_name }}</b>
          will have until
          <b>{{ Intl.DateTimeFormat().format(endDate) }}</b>
          to create events of this discipline.
        </p>
        <p class="remove-notice">
          <b>{{ clinician.person?.display_name }}</b>
          will have until
          <b>{{ Intl.DateTimeFormat().format(serviceEndDate) }}</b>
          to document events of this discipline.
        </p>
      </div>
    </div>
  </ConfirmAction>
</template>

<style scoped lang="scss">
.remove-provider-modal {
  label {
    color: $grayDark;
    font-size: 0.75rem;
    span.sub-text {
      color: $darkMedium;
    }
  }
  .remove-notice {
    color: $grayDark !important;
  }
}
</style>
